class QuickbuyDrawer extends Drawer {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', (event) => {
      if (event.target.classList.contains('klaviyo-bis-trigger')) {
        event.preventDefault();
        const klaviyoBISId = this.getAttribute('data-klaviyo-bis-id');
        if (!klaviyoBISId) {
          console.error('Klaviyo BIS ID not found');
          return;
        } else {
          const backInStockEl = document.querySelector(`#${klaviyoBISId}`);

          if (backInStockEl.querySelector('a')) {
            backInStockEl.querySelector('a')?.click();
          } else {
            const template = backInStockEl.parentElement.querySelector('.klaviyo-bis-template');
            if (template) {
              const templateHTML = template.innerHTML;
              backInStockEl.innerHTML = templateHTML;
              backInStockEl.querySelector('a')?.click();
            }
          }
        }
      }
    });
  }
}
customElements.define('quickbuy-drawer', QuickbuyDrawer);

class HotspotQuickbuyDrawer extends QuickbuyDrawer {
  #bannerResizeObserver = null;
  #boundUpdateHeight = null;

  connectedCallback() {
    super.connectedCallback();

    this.#boundUpdateHeight = this.updateBannerMaxHeight.bind(this);
    this.setupBannerHeightObserver();

    this.updateBannerMaxHeight();
  }

  open(triggeredBy) {

    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.stickProductGridTopBar();

    document.querySelector('drawer.active')?.close();
    document.documentElement.classList.add('drawer--isActive');
    document.documentElement.classList.add(`${this.drawerTagName}--isActive`);
    this.classList.add('active');

    this.updateBannerMaxHeight();

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : this.firstElementChild;
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    this.dispatchEvent(new CustomEvent(`${this.drawerTagName}:toggle`, { detail: { action: 'open' } }));
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.documentElement.classList.remove('drawer--isActive');
    document.documentElement.classList.remove(`${this.drawerTagName}--isActive`);

    this.unstickProductGridTopBar();

    this.dispatchEvent(new CustomEvent(`${this.drawerTagName}:toggle`, { detail: { action: 'close' } }));
  }

  disconnectedCallback() {
    this.teardownBannerHeightObserver();
  }

  getClosestBannerElement() {
    return this.closest('.hotspot-media-banner');
  }

  updateBannerMaxHeight() {
    const banner = this.getClosestBannerElement();
    let heightPx = Math.round((banner?.getBoundingClientRect().height) || window.innerHeight);

    if (!heightPx || heightPx <= 0) {
      heightPx = Math.round(window.innerHeight);
    }

    this.style.setProperty('--hotspot-banner-height', `${heightPx}px`);
  }

  setupBannerHeightObserver() {
    const banner = this.getClosestBannerElement();

    window.addEventListener('resize', this.#boundUpdateHeight, { passive: true });

    if (banner && 'ResizeObserver' in window) {
      this.#bannerResizeObserver = new ResizeObserver(() => {
        this.updateBannerMaxHeight();
      });
      this.#bannerResizeObserver.observe(banner);
    }
  }

  teardownBannerHeightObserver() {
    window.removeEventListener('resize', this.#boundUpdateHeight);
    if (this.#bannerResizeObserver) {
      try {
        this.#bannerResizeObserver.disconnect();
      } catch (e) {}
      this.#bannerResizeObserver = null;
    }
  }
}
customElements.define('hotspot-quickbuy-drawer', HotspotQuickbuyDrawer);

class QuickbuyDrawerButton extends DrawerButton {
  drawerTag = null;
  drawer = null;

  connectedCallback() {
    this.drawerTag = this.dataset.drawerTag;
    if (!this.drawerTag) {
      return;
    }

    this.drawer = !!this.closest('.hotspot-media-banner') ? this.closest('.hotspot-media-banner').querySelector(this.drawerTag) : document.querySelector(this.drawerTag);

    if (!this.drawer) {
      return;
    }

    this.addEventListener('click', () => {
      this.handleClick();
    });

    this.drawer?.addEventListener(`${this.drawerTag}:toggle`, (evt) => {
      if (evt.detail.action === 'open') {
        this.dataset.action = true;
      } else {
        this.dataset.action = '';
      }
    });
  }

  handleClick() {
    this.quickbuyDrawer = !!this.closest('.hotspot-media-banner') ? this.closest('.hotspot-media-banner').querySelector('hotspot-quickbuy-drawer .drawer__content') : document.querySelector('quickbuy-drawer .drawer__content');

    const klaviyoBISId = this.getAttribute('data-klaviyo-bis-id');
    if (klaviyoBISId) {
      this.quickbuyDrawer.closest('.drawer')?.setAttribute('data-klaviyo-bis-id', klaviyoBISId);
    }

    this.show();
  }

  show() {
    let cleanProductUrl = this.getAttribute('data-product-url').split('?')[0];
    fetch(cleanProductUrl + '?view=quickbuy')
      .then((response) => response.text())
      .then((responseText) => {
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        this.productElement = responseHTML.querySelector('quickbuy-content');
      })
      .finally(() => {
        this.drawer.open();
        this.setInnerHTML(this.quickbuyDrawer, this.productElement.innerHTML);
        // Only move footer content for hotspot drawers
        if (this.drawer.tagName.toLowerCase() === 'hotspot-quickbuy-drawer') {
          this.moveQuickbuyFooterContent();
        }
        this.preventVariantURLSwitching();
        this.renderTrustPilotBadges();
        if (uaKlaviyo) {
          uaKlaviyo.renderQuickBuyKlaviyoBIS(this.quickbuyDrawer);
        }

        // Datalayer tracking
        document.dispatchEvent(new CustomEvent('update-tracking-buttons'));
      });
  }

  renderTrustPilotBadges() {
    document.dispatchEvent(new CustomEvent('trustpilot:render-product-mini-widgets', {
      detail: {
        productContainerSelector: 'quickbuy-drawer'
      }
    }));
  }

  setInnerHTML(element, html) {
    element.innerHTML = html;

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));

      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }

  preventVariantURLSwitching() {
    const variantPicker = this.quickbuyDrawer.querySelector('variant-radios,variant-selects');
    if (!variantPicker) return;

    variantPicker.setAttribute('data-update-url', 'false');
  }

  moveQuickbuyFooterContent() {
    // Move the purchase form and related status elements out of the scrollable area into the drawer footer
    if (!this.drawer) return;
    const drawerInner = this.drawer.querySelector('.drawer__inner');
    const content = drawerInner?.querySelector('.drawer__content');
    if (!drawerInner || !content) return;

    const productForm = content.querySelector('product-form');
    if (!productForm) return;

    // Keep rewards messaging in the scrollable content for hotspot quickbuy
    const rewardMessages = Array.from(productForm.querySelectorAll('.pdp-rewards-messages'));
    if (rewardMessages.length && productForm.parentElement) {
      rewardMessages.forEach((messageEl) => {
        productForm.parentElement.insertBefore(messageEl, productForm);
      });
    }

    const extras = [];
    const extraSelectors = [
      '.product-form__error-message-wrapper',
      '.product-form__quantity-message',
      'pickup-availability'
    ];

    extraSelectors.forEach((selector) => {
      const el = content.querySelector(selector);
      if (el) extras.push(el);
    });

    let footer = drawerInner.querySelector('.drawer__footer');
    if (!footer) {
      footer = document.createElement('div');
      footer.className = 'drawer__footer';
      drawerInner.appendChild(footer);
    }

    footer.innerHTML = '';

    footer.appendChild(productForm);
    extras.forEach((el) => footer.appendChild(el));
  }
}

customElements.define('quickbuy-button', QuickbuyDrawerButton);
